import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Plus, Trash2, Search } from 'lucide-react';

interface Treatment {
  id: string;
  name: string;
  description: string;
  price: string;
}

interface TreatmentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  treatments: Treatment[];
  onChange: (treatments: Treatment[]) => void;
}

const TreatmentsDialog: React.FC<TreatmentsDialogProps> = ({
  isOpen,
  onClose,
  treatments,
  onChange,
}) => {
  const [localTreatments, setLocalTreatments] = useState<Treatment[]>(treatments);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalTreatments(treatments);
    setHasUnsavedChanges(false);
  }, [treatments]);

  // Check for unsaved changes whenever localTreatments changes
  useEffect(() => {
    const hasChanges = JSON.stringify(localTreatments) !== JSON.stringify(treatments);
    setHasUnsavedChanges(hasChanges);
  }, [localTreatments, treatments]);

  const handleCancel = useCallback(() => {
    setLocalTreatments(treatments);
    setSearchTerm('');
    setHasUnsavedChanges(false);
    onClose();
  }, [treatments, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleCancel]);

  // Filter treatments based on search term
  const filteredTreatments = localTreatments.filter(treatment =>
    treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    treatment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    treatment.price.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addTreatment = () => {
    const newTreatment: Treatment = {
      id: `treatment-${Date.now()}`,
      name: '',
      description: '',
      price: '',
    };
    setLocalTreatments([...localTreatments, newTreatment]);
  };

  const removeTreatment = (id: string) => {
    setLocalTreatments(localTreatments.filter(treatment => treatment.id !== id));
  };

  const updateTreatment = (id: string, field: keyof Treatment, value: string) => {
    setLocalTreatments(localTreatments.map(treatment =>
      treatment.id === id ? { ...treatment, [field]: value } : treatment
    ));
  };

  const handleSave = () => {
    // Filter out empty treatments
    const validTreatments = localTreatments.filter(
      treatment => treatment.name.trim() !== ''
    );
    onChange(validTreatments);
    setHasUnsavedChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div ref={dialogRef} className="bg-white p-8 rounded-lg shadow-lg w-full max-w-4xl overflow-y-auto max-h-[80vh] relative">
        {/* Toast-style Save Button - appears when there are unsaved changes */}
        {hasUnsavedChanges && (
          <div className="fixed bottom-8 right-8 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-5 min-w-[280px]">
            <div className="flex flex-col space-y-3">
              <p className="text-base text-gray-700 font-semibold">You have unsaved changes</p>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-3 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 bg-brutal-dark text-white px-4 py-3 text-sm rounded hover:bg-brutal-charcoal transition font-medium"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Manage Treatments</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search treatments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] hover:border-[#FF6B35] transition-colors"
            />
          </div>
        </div>

        <div className="space-y-6">
          {filteredTreatments.map((treatment, index) => (
            <div key={treatment.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">Treatment {localTreatments.indexOf(treatment) + 1}</h3>
                <button
                  onClick={() => removeTreatment(treatment.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Treatment Name</label>
                  <input
                    type="text"
                    value={treatment.name}
                    onChange={(e) => updateTreatment(treatment.id, 'name', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., Swedish Massage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Price</label>
                  <input
                    type="text"
                    value={treatment.price}
                    onChange={(e) => updateTreatment(treatment.id, 'price', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., $120"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-semibold mb-2">Description</label>
                <textarea
                  value={treatment.description}
                  onChange={(e) => updateTreatment(treatment.id, 'description', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-32 resize-none"
                  placeholder="Describe this treatment..."
                />
              </div>
            </div>
          ))}

          {filteredTreatments.length === 0 && searchTerm && (
            <div className="text-center py-8 text-gray-500">
              No treatments found matching "{searchTerm}"
            </div>
          )}

          <button
            onClick={addTreatment}
            className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-gray-500 hover:border-gray-400 hover:text-gray-600 flex items-center justify-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add New Treatment
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreatmentsDialog;